/**
 * BFF (Backend for Frontend) API Client
 * Handles communication with the chat BFF service
 */

const { REACT_APP_BFF_URL } = process.env;
const BFF_URL = REACT_APP_BFF_URL || "https://demo.tenoro.io/agent-bff";

class BFFApiError extends Error {
  constructor(message, status, response) {
    super(message);
    this.name = "BFFApiError";
    this.status = status;
    this.response = response;
  }
}

/**
 * BFF API Client
 */
export class BFFApiClient {
  constructor(baseUrl = BFF_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Make a request to the BFF API
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        let errorData = null;

        try {
          errorData = await response.json();
          if (errorData.error) {
            errorMessage =
              typeof errorData.error === "string"
                ? errorData.error
                : errorData.error.message || errorMessage;
          }
        } catch {
          // If we can't parse JSON, use the original error message
        }

        throw new BFFApiError(errorMessage, response.status, errorData);
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        return await response.json();
      }

      return await response.text();
    } catch (error) {
      if (error instanceof BFFApiError) {
        throw error;
      }

      // Handle network errors, etc.
      throw new BFFApiError(`Network error: ${error.message}`, 0, null);
    }
  }

  /**
   * Get MCP status and available tools
   */
  async getMCPStatus({ apiKey = null }) {
    const headers = {
      "content-type": "application/json",
      Authorization: `Basic ${apiKey}`,
    };
    return await this.request("/status", {
      method: "GET",
      headers,
    });
  }

  /**
   * Send a chat completion request with a single message
   * The backend now expects a single message and handles tool execution automatically
   */
  async chatCompletion({
    message,
    conversationId = null,
    apiKey = null,
    model = null,
    prompt = null,
  }) {
    const headers = {
      "content-type": "application/json",
    };
    if (apiKey) {
      headers.Authorization = `Basic ${apiKey}`;
    }
    const body = { message, conversationId };

    if (model) {
      body.model = model;
    }
    if (prompt) {
      body.prompt = prompt;
    }
    return await this.request("/chat", {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
  }
}

/**
 * Default BFF API client instance
 */
export const bffApi = new BFFApiClient();

/**
 * Utility functions
 */

/**
 * Create a message object in internal format
 */
export function createMessage({
  text,
  isBot = false,
  isError = false,
  map = null,
  image = null,
  badge = null,
}) {
  return {
    id: Date.now() + Math.random(), // Ensure uniqueness
    text,
    isBot,
    isError,
    map,
    image,
    badge,
    timestamp: new Date(),
  };
}

/**
 * Handle BFF API errors gracefully
 */
export function handleBFFError(
  error,
  defaultMessage = "An unexpected error occurred",
) {
  console.error("BFF API Error:", error);

  if (error instanceof BFFApiError) {
    return error.message;
  }

  if (error.message) {
    return error.message;
  }

  return defaultMessage;
}

export default bffApi;
